import { Button, Modal } from '@components/common';
import {
  FormInput,
  FormSelect,
  FormTextarea,
} from '@components/common/FormField';
import React, { useMemo, useState } from 'react';
import { AccountRecord } from '../../../../types/accounting';
import {
  TAX_APPLICATION_OPTIONS,
  TAX_STATUS_OPTIONS,
  TaxRateFormState,
} from './taxRates';

interface TaxRateFormModalProps {
  initial: TaxRateFormState;
  title: string;
  accounts: AccountRecord[];
  isSaving: boolean;
  onClose: () => void;
  onSubmit: (form: TaxRateFormState) => void;
}

export function TaxRateFormModal({
  initial,
  title,
  accounts,
  isSaving,
  onClose,
  onSubmit,
}: TaxRateFormModalProps) {
  const [form, setForm] = useState<TaxRateFormState>(initial);

  const accountOptions = useMemo(
    () => accounts.filter((account) => account.is_active),
    [accounts],
  );

  const setField = <K extends keyof TaxRateFormState>(
    key: K,
    value: TaxRateFormState[K],
  ) => setForm((current) => ({ ...current, [key]: value }));

  return (
    <Modal isOpen onClose={onClose} title={title} size="lg">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(form);
        }}
        className="space-y-4"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <FormInput
            label="Tax code"
            required
            value={form.code}
            onChange={(event) => setField('code', event.target.value)}
            placeholder="e.g. VAT"
          />
          <FormInput
            label="Tax name"
            required
            value={form.name}
            onChange={(event) => setField('name', event.target.value)}
            placeholder="e.g. Value Added Tax"
          />
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <FormInput
            label="Rate (%)"
            type="number"
            min="0"
            step="0.0001"
            required
            value={form.rate}
            onChange={(event) => setField('rate', event.target.value)}
            placeholder="0.0000"
          />
          <FormSelect
            label="Application"
            required
            value={form.application}
            onChange={(event) =>
              setField(
                'application',
                event.target.value as TaxRateFormState['application'],
              )
            }
          >
            {TAX_APPLICATION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </FormSelect>
          <FormSelect
            label="Status"
            required
            value={form.status}
            onChange={(event) =>
              setField(
                'status',
                event.target.value as TaxRateFormState['status'],
              )
            }
          >
            {TAX_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </FormSelect>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <FormSelect
            label="Output tax account"
            value={form.outputAccountId}
            onChange={(event) =>
              setField('outputAccountId', event.target.value)
            }
          >
            <option value="">No output account</option>
            {accountOptions.map((account) => (
              <option key={account.id} value={account.id}>
                {account.code} - {account.name}
              </option>
            ))}
          </FormSelect>
          <FormSelect
            label="Input tax account"
            value={form.inputAccountId}
            onChange={(event) => setField('inputAccountId', event.target.value)}
          >
            <option value="">No input account</option>
            {accountOptions.map((account) => (
              <option key={account.id} value={account.id}>
                {account.code} - {account.name}
              </option>
            ))}
          </FormSelect>
        </div>

        <FormTextarea
          label="Description"
          value={form.description}
          onChange={(event) => setField('description', event.target.value)}
          rows={4}
          placeholder="Optional description for this tax rate"
        />

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSaving}>
            Save tax rate
          </Button>
        </div>
      </form>
    </Modal>
  );
}
